import { Observable, catchError, map, of } from 'rxjs'
import { Injectable } from "@angular/core"

import { IUpdateWalletSettings, IWalletSettings } from '@modules/private/wallet/data/interfaces/wallet.interface';
import { IWalletList } from "@modules/private/wallet/data/interfaces/iwalllet.interface"
import { IResponseArray } from "@data/interfaces/iresponseArray.interface"
import { AuthService } from "@data/services/modules/auth/auth.service"
import { ApiClass } from "@data/class/api-class"
import { environment } from "@env/environment"
import { UserRole } from "@data/enums/user-role.enum"

/**
 * Una condición vacía se OMITE, no se manda en `null`: la última regla de la matriz es «la que recibe
 * quien no cumple ninguna otra», y el backend la reconoce porque sus tres condiciones vienen sin
 * valor. Mandar `null` la haría parecer una regla con condiciones y rechazaría el guardado entero.
 */
const sinCondicionesVacias = (regla: Record<string, any>): Record<string, any> => Object.fromEntries(Object.entries(regla).filter(([, v]) => v !== null && v !== undefined))

@Injectable({
  providedIn: 'root'
})
export class ApiWalletService extends ApiClass {
  walletId!: string
  role!: string;
  userId!: string;
  wallet: any = {}

  constructor(private readonly authService: AuthService) {
    super();
    this.path = 'wallet';
    this.role = this.authService.getUser?.user.permissions.name || '';
    this.userId = this.authService.getUser?.user.id || '';
  }

  getAll(page = 1, limit = 10, filters = {}) {
    filters = {
      ...filters,
      status: { value: 'Activo', matchMode: 'equals' },
      walletId: { value: this.walletId, matchMode: 'equals' }
    };

    return this.http.post<IResponseArray>(environment.apiUrl + this.path, { page, limit, filters }).pipe(
      map((r) => r as IWalletList),
      catchError(() => of({ error: true, data: [], total: 0 } as IWalletList))
    );
  }

  getWalletAmounts() {
    let requestBody = {};
    if (this.role !== UserRole.ADMIN) requestBody = { id: this.userId };

    return this.http.post<{ data: any }>(`${environment.apiUrl}${this.path}/amounts`, requestBody)
      .pipe(
        map(response => response.data || []),
        catchError(() => of([]))
      );
  }

  getAdminWalletAmounts() {
    return this.http.post<{ data: any }>(`${environment.apiUrl}${this.path}/adminAmounts`, {})
      .pipe(
        map(response => response.data || []),
        catchError(() => of([]))
      );
  }

  getOneWalletAmounts() {
    return this.http.post<{ data: any }>(`${environment.apiUrl}${this.path}/amounts/${this.walletId}`, {})
      .pipe(
        map(response => response.data || {}),
        catchError(() => of({}))
      );
  }

  /**
   * Reglas de cartera (F21, F30): interruptor de mora, qué hacer al apagarla, tasa default, días de
   * gracia, ventana de duplicados y matriz de calificación. Con `history = true` trae el historial,
   * que solo consume la pestaña Reglas.
   */
  getSettings(history = false): Observable<{ error: boolean, msg: string, data: IWalletSettings | null }> {
    const query = history ? '?history=true' : ''

    return this.http.get(`${environment.apiUrl}${this.path}/settings${query}`).pipe(
      map((r) => r as { error: boolean, msg: string, data: IWalletSettings }),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudieron cargar las reglas de cartera', data: null }))
    )
  }

  /**
   * Guarda las reglas. Solo Administración tiene `wallet.settings`: Finanzas y Operaciones reciben
   * 403 aunque vean la pestaña. Cada campo que cambia deja su propia entrada en el historial.
   *
   * ⚠️ **Lo que se lee no se puede devolver tal cual.** Cada regla de la matriz llega con el `_id`
   * que Mongoose le pone a todo subdocumento, y el DTO del backend no lo declara: con
   * `forbidNonWhitelisted` activo, reenviarlo devuelve **400 en TODO guardado** de esta pantalla —el
   * interruptor de mora incluido— con el mensaje inútil «Bad Request Exception» y el detalle real
   * escondido en `data.errors`. Se manda solo lo que el contrato declara.
   */
  putSettings(payload: IUpdateWalletSettings): Observable<{ error: boolean, msg: string, data: IWalletSettings | null }> {
    const limpio = {
      ...payload,
      ...(payload.paymentQuality
        ? {
            paymentQuality: {
              ...payload.paymentQuality,
              ...(payload.paymentQuality.grades
                ? { grades: payload.paymentQuality.grades.map((g: any) => sinCondicionesVacias({ grade: g.grade, minOnTimeRate: g.minOnTimeRate, maxDelayDays: g.maxDelayDays, maxOpenOverdue: g.maxOpenOverdue })) }
                : {}),
            },
          }
        : {}),
    }

    return this.http.put(`${environment.apiUrl}${this.path}/settings`, limpio).pipe(
      map((r) => r as { error: boolean, msg: string, data: IWalletSettings }),
      catchError((e) => of({ error: true, msg: e?.error?.msg || 'No se pudieron guardar las reglas de cartera', data: null }))
    )
  }

}
